/* eslint-disable react-hooks/exhaustive-deps */
import { Form, Formik } from 'formik';
import { useEffect, useState, useMemo } from 'react';

import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import { Fields, validationSchema } from './validation';
import ContractDropdownHeaderItem from '@/components/common/ContractInputHeaderItem/ContractDropdownHeaderItem';
import { PurchaseOrderOutputType } from '@/graphql/contracts/_generated_/contracts';
import ActionButtonsWrapper from '@/components/common/ModalWindow/ActionButtonsWrapper/ActionButtonsWrapper';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import useGetPurchaseOrders from '@/pages/ContractDetails/ContractLines/hooks/useGetPurchaseOrders';
import { useGetCustomerName } from '@/routing/breadcrumbs/useGetCustomerName';
import ContractHeaderItem from '@/components/common/ContractHeaderItem/ContractHeaderInputItem';
import ModalFormDatepicker from '@/components/business/ModalForm/ModalFormDatepicker/ModalFormDatepicker';
import ModalFormTextInput from '@/components/business/ModalForm/ModalFormTextInput/ModalFormTextInput';
import ModalFormTextarea from '@/components/business/ModalForm/ModalFormTextarea/ModalFormTextarea';
import { isValidDate } from '@/utils/date.utils';
import { NumberUtils } from '@/utils/numbers.utils';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import { useLoadingAnimation } from '@/components/LoadingAnimation/useLoadingAnimation.hook';
import useGetContractDetails from '@/hooks/contract/useGetContractDetails.hook';
// eslint-disable-next-line max-len
import AddPurchaseOrderModal from '@/pages/ContractDetails/RightPanel/Billing/AssignPurchaseOrderForLines/components/AddPurchaseOrderModal/AddPurchaseOrderModal';
import styles from './assign-purchase-order.module.scss';

interface IAssignPurchaseOrderModalProps {
  onClose: () => void;
  onSubmit: (values: Fields) => void;
  invoiceToCustomerId: string;
  totalLinesCount: number;
  totalLinesWithPoCount: number;
  contractId?: UUID;
  contractCurrency?: string;
}

export default function AssignPurchaseOrderModal(
  { onClose, onSubmit, invoiceToCustomerId, totalLinesCount, totalLinesWithPoCount, contractId, contractCurrency }:
  IAssignPurchaseOrderModalProps) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { contract } = contractId ? useGetContractDetails(contractId) : { contract: null };
  const {
    getPurchaseOrdersItems,
    getPurchaseOrdersLoading
  } = useGetPurchaseOrders();
  const { customerName, customerCode, getCustomerName } = useGetCustomerName();
  const currency = contractCurrency ? contractCurrency : contract?.currency?.currency;
  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrderOutputType | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isAddPurchaseOrderModalOpen, setIsAddPurchaseOrderModalOpen] = useState(false);
  const [typedPoNumber, setTypedPoNumber] = useState('');
  const [formFieldSetter, setFormFieldSetter] = useState<((field: string, value: any) => void) | null>(null);
  const { toggleLoadingAnimation } = useLoadingAnimation();
  const [purchaseOrdersData, setPurchaseOrdersData] = useState<PurchaseOrderOutputType[]>([]);
  const [noPurchaseOrdersFound, setNoPurchaseOrdersFound] = useState(false);
  const selectedPoNumberDate = (poNumberDate: Date) => {
    return isValidDate(poNumberDate) ? new Date(poNumberDate) : undefined;
  };

  const createNewPoOption = (searchString: string): PurchaseOrderOutputType => ({
    id: 'create-new-po',
    poNumber: `Create new PO: ${searchString}`,
    isCustomOption: true,
    typedValue: searchString
  } as any);

  useEffect(() => {
    const fetchPurchaseOrders = async () => {
      toggleLoadingAnimation(true);
      getCustomerName(invoiceToCustomerId);
      if (customerName) {
        const purchaseOrders = await getPurchaseOrdersItems({
          filter: {
            customerName: { eq: customerName },
            poCurrency: { eq: contract?.currency.currency }
          },
          sort: [{ field: 'Created', order: 'Ascending' }]
        });

        const purchaseOrdersResultData =
          purchaseOrders?.map((el) => el ? ({
            ...el,
          }) : el) ?? [];

        if (purchaseOrdersResultData.length === 0) {
          setNoPurchaseOrdersFound(true);
        }
        else {
          setPurchaseOrdersData(purchaseOrdersResultData as PurchaseOrderOutputType[]);
        }
        toggleLoadingAnimation(false);
      }
    };
    fetchPurchaseOrders();
  }, [invoiceToCustomerId, customerName]);

  const enhancedPurchaseOrdersData = useMemo(() => {
    if (typedPoNumber.length > 0) {
      const filteredOptions = purchaseOrdersData.filter((po) =>
        po?.poNumber?.toLowerCase().includes(typedPoNumber.toLowerCase())
      );
      const newPoOption = createNewPoOption(typedPoNumber);
      return [...filteredOptions, newPoOption];
    }
    return purchaseOrdersData;
  }, [purchaseOrdersData, typedPoNumber]);

  useEffect(() => {
    toggleLoadingAnimation(getPurchaseOrdersLoading);
  }, [getPurchaseOrdersLoading]);

  const handleAddPurchaseOrderModalClose = () => {
    setIsAddPurchaseOrderModalOpen(false);
    setTypedPoNumber('');
  };

  const handleAddPurchaseOrderModalSuccess = (createdPurchaseOrder: any) => {
    setPurchaseOrder(createdPurchaseOrder);
    if (formFieldSetter) {
      formFieldSetter('purchaseOrderId', createdPurchaseOrder?.id);
    }
    setIsAddPurchaseOrderModalOpen(false);
    setTypedPoNumber('');
    setShowConfirmationModal(true);
  };

  return (
    <>
      {purchaseOrdersData.length > 0 &&
        < AgModalWindow
          title={`Assign Purchase Order ${totalLinesCount > 1 ? ` to ${totalLinesCount} lines` :
            totalLinesCount > 0 ? ` to ${totalLinesCount} line` : ''}`
          }
          isOpen={true}
          onClose={onClose}
          contentStyle={{ minWidth: '500px', maxWidth: '600px', width: '560px' }} >

          <Formik<Fields>
            onSubmit={() => {
              setShowConfirmationModal(true);
            }}
            initialValues={{ purchaseOrderId: '' }}
            validateOnMount={true}
            validationSchema={validationSchema}
          >
            {({ values, setFieldValue, dirty, isValid }) => {
              if (formFieldSetter !== setFieldValue) {
                setFormFieldSetter(() => setFieldValue);
              }
              return (
                <Form>
                  <AgConfirmationModal
                    isOpen={showConfirmationModal && totalLinesCount > 0}
                    title={`The change will affect ${totalLinesCount} of contract lines(s)`}
                    onClose={() => setShowConfirmationModal(false)}
                    onConfirm={() => {
                      onSubmit(values);
                    }}
                    contentStyle={{ width: 600 }}
                  >
                    While assigning the PO number to {totalLinesCount} contract line(s),
                    you are about to replace existing PO number for &nbsp;
                    {totalLinesWithPoCount} contract line(s).<br />
                    Please confirm you wish to continue.
                  </AgConfirmationModal>

                  <div className={styles['dropdown-margin']}>
                    <ContractDropdownHeaderItem<PurchaseOrderOutputType>
                      isSearchable={true}
                      isClearable={true}
                      required={true}
                      label={'Purchase Order'}
                      optionsData={enhancedPurchaseOrdersData}
                      getOptionLabel={(option) => option?.poNumber ?? ''}
                      getOptionValue={(option) => option?.poNumber ?? ''}
                      placeholder={purchaseOrdersData[0]?.poNumber ?? ''}
                      handleInputChange={(value) => {
                        setTypedPoNumber(value);
                      }}
                      handleChange={(option) => {
                        if (option && (option as any).isCustomOption) {
                          setIsAddPurchaseOrderModalOpen(true);
                          return;
                        }
                        setPurchaseOrder(option);
                        setFieldValue('purchaseOrderId', option?.id);
                      }}
                    />

                    <div >
                      <ModalFormTextInput
                        id="customerInfo"
                        labelTitle="Selected Customer"
                        value={`${customerName} (${customerCode})`}
                        disabled={true}
                      />
                    </div>

                    <div className={styles['text-wrapper']}>
                      <ModalFormTextInput
                        id="poNumber"
                        labelTitle="Number"
                        value={purchaseOrder?.poNumber ?? ''}
                        disabled={true}
                      />
                      <ModalFormTextInput
                        id="poCurrency"
                        labelTitle="Currency"
                        value={purchaseOrder?.poCurrency ?? ''}
                        disabled={true}
                      />
                    </div>

                    <div className={styles['text-wrapper']}>
                      <ContractHeaderItem
                        label="Total Value"
                        value={NumberUtils.prettifyWithRound(purchaseOrder?.poTotalValue) ?? ''}
                        disabled
                        inputStyle={{ width: '100%', textAlign: 'right' }}
                        defaultValue={purchaseOrder?.poTotalValue ?? ''}
                      />
                      <ContractHeaderItem
                        label="Remaining Value"
                        value={NumberUtils.prettifyWithRound(purchaseOrder?.poRemainingValue) ?? ''}
                        disabled
                        inputStyle={{ width: '100%', textAlign: 'right' }}
                        defaultValue={purchaseOrder?.poRemainingValue ?? ''}
                      />
                    </div>

                    <div className={styles['text-wrapper']}>
                      <ModalFormDatepicker
                        id="poDate"
                        labelTitle="Date"
                        value={selectedPoNumberDate(purchaseOrder?.poDate)}
                        onChange={() => { }}
                        disabled={true}
                      />
                      <ModalFormDatepicker
                        id="ponumberStartDate"
                        labelTitle="Start Date"
                        value={selectedPoNumberDate(purchaseOrder?.poStartDate)}
                        onChange={() => { }}
                        disabled={true}
                      />
                      <ModalFormDatepicker
                        id="ponumberEndDate"
                        labelTitle="End Date"
                        value={selectedPoNumberDate(purchaseOrder?.poEndDate)}
                        onChange={() => { }}
                        disabled={true}
                      />
                    </div>

                    <div className={styles['text-wrapper']}>
                      <ModalFormTextInput
                        id="poStatus"
                        labelTitle="Status"
                        value={purchaseOrder?.poStatus ?? ''}
                        disabled={true}
                      />
                      <div></div>
                    </div>

                    <div  >
                      <ModalFormTextarea
                        id="poNote"
                        labelTitle="Note"
                        value={purchaseOrder?.poNote ?? ''}
                        disabled={true}
                      />
                    </div>

                  </div>
                  <ActionButtonsWrapper
                    leftButton={
                      <Button
                        buttonStyleType={ButtonStyleType.SECONDARY}
                        onClick={onClose}
                      > Cancel</Button>
                    }
                    rightButton={
                      <Button
                        disabled={!isValid || !dirty}
                        buttonStyleType={ButtonStyleType.PRIMARY}
                        type='submit'
                      > Save</Button>
                    }
                  />
                </Form>
              );
            }}
          </Formik>
        </AgModalWindow >
      }

      {noPurchaseOrdersFound &&
        < AgModalWindow
          title={'Assign Purchase Order'}
          isOpen={true}
          onClose={onClose}
          contentStyle={{ minWidth: '500px', maxWidth: '600px', width: '560px' }} >
          <span>
            Invoice to Account does not have any purchase orders.
          </span>
        </AgModalWindow >}

      <AddPurchaseOrderModal
        isOpen={isAddPurchaseOrderModalOpen}
        onClose={handleAddPurchaseOrderModalClose}
        onSuccess={handleAddPurchaseOrderModalSuccess}
        initialPoNumber={typedPoNumber}
        customerId={invoiceToCustomerId}
        customerName={customerName}
        customerCode={customerCode}
        currency={currency}
      />
    </>
  );
}
